import * as React from 'react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@rbilabs/components-library';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { superstruct } from 'superstruct';

import { Accordion, AccordionItem } from 'components/accordion';
import { ActionButtonSizes, ActionButtonVariants, ActionLink } from 'components/action-button';
import { CreateSupportCaseFeedback } from 'components/create-support-case-feedback';
import { FormikCheckbox } from 'components/formik/checkbox';
import { FormikDateInput } from 'components/formik/date-input';
import { FormikSelect } from 'components/formik/select';
import { FormikTextArea } from 'components/formik/text-area';
import { FormikTextInput } from 'components/formik/text-input';
import { GoogleGeolocationLibraryLoader } from 'components/google-geolocation-library-loader';
import { MobileSpacer } from 'components/mobile-spacer';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { ALERT_STYLE, SupportAlertBox } from 'components/support-alert-box';
import { TermsOfServiceLabel } from 'components/user-info-form/checkbox-labels';
import { IRestaurantFragment, useGetRestaurantsQuery } from 'generated/sanity-graphql';
import { useKeyboardState } from 'hooks/use-keyboard-state';
import { simplifiedServiceModesForSupport } from 'pages/support-v2/constants';
import { CreateCaseState, ISupportFormData } from 'state/knowledge-force/types';
import { serializeServiceMode } from 'state/service-mode';
import {
  getFormErrorsForStruct,
  isEmailValid,
  requiredBoolean,
  requiredString,
  requiredValidDateObject,
} from 'utils/form';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import { StoreSelector } from '../store-selector';
import {
  ContentWrap,
  NeedHelpHeading,
  PageHeading,
  SectionHeading,
} from '../support-form-v2.styled';

import { StyledActionButton } from './styled';
import { IFaq, ISupportV2FormViewProps } from './types';

const getAddress = (store: IRestaurantFragment) => {
  const addressFields = ['address1', 'address2', 'city', 'state', 'country', 'zip'];
  const address = store.physicalAddress ?? {};
  return addressFields
    .map(field => address[field])
    .filter(Boolean)
    .join(', ');
};

export const SupportV2FormView: FC<ISupportV2FormViewProps> = ({
  alertMessage,
  bigTitle,
  caseReferenceNumber,
  createCaseError,
  createCaseState,
  faqs,
  handleSupportFormSubmit,
  initialValues,
  onFaqInteraction = noop,
  orderDate,
  serviceMode,
  smallTitle,
  useHighVolumeSupportMessage,
  showRestaurantSelector,
  showServiceModeSelector,
  fromApp,
}) => {
  const { formatDate, formatMessage, formatTime } = useIntl();
  const { isKeyboardOpen, keyboardHeight } = useKeyboardState();

  const errorMessageByType = useMemo(
    () => ({
      requiredString: formatMessage({ id: 'requiredField' }),
      requiredBoolean: formatMessage({ id: 'requiredField' }),
      requiredValidDateObject: formatMessage({ id: 'requiredDate' }),
      optionalString: 'this should never render, but still needs to be part of the struct',
    }),
    [formatMessage]
  );

  const struct = useMemo(
    () =>
      superstruct({
        types: {
          requiredBoolean,
          requiredString,
          requiredValidDateObject,
          optionalString: (value: string) => !value || value.length > 0,
        },
      }),
    []
  );

  const valueValidationObject = useMemo(
    () =>
      struct({
        comments: 'requiredString',
        email: 'requiredString',
        issueId: 'requiredString',
        issueSubCategory: 'requiredString',
        name: 'requiredString',
        orderDate: 'optionalString',
        agreesToTermsOfService: 'requiredBoolean',
        serviceMode: 'optionalString',
        ...(!!showRestaurantSelector && {
          storeId: 'requiredString',
          storeAddress: 'requiredString',
          orderDateTime: 'requiredValidDateObject',
        }),
      }),
    [showRestaurantSelector, struct]
  );

  const getFormErrors = getFormErrorsForStruct<ISupportFormData, typeof errorMessageByType>(
    valueValidationObject,
    errorMessageByType
  );

  const feedbackRef = useRef<HTMLParagraphElement>(null);

  const locationFieldRef = useRef<HTMLInputElement>(null);
  const [isRestaurantSelectorShowing, setIsRestaurantSelectorShowing] = useState(false);
  const { refetch: fetchRestaurants } = useGetRestaurantsQuery({ skip: true });
  const [selectedStoreAddress, setSelectedStoreAddress] = useState<string>(
    formatMessage({ id: 'clickToSelectLocation' })
  );

  const updateStore = useCallback(
    async (storeId: string, setField: (field: string, value: string) => void) => {
      const { data: stores } = await fetchRestaurants({ filter: { _id: storeId }, limit: 1 });
      const newStore = stores?.allRestaurants?.[0];
      if (newStore) {
        const newAddress = getAddress(newStore);
        setSelectedStoreAddress(newAddress);
        setField('storeId', newStore.number ?? '');
        setField('storeAddress', newAddress);
      }
    },
    [fetchRestaurants]
  );

  const validateForm = (values: ISupportFormData) => {
    const invalidFormatEmailError: { email?: string } = {};
    if (!isEmailValid(values.email)) {
      invalidFormatEmailError.email = formatMessage({ id: 'notValidEmailError' });
    }
    return { ...invalidFormatEmailError, ...getFormErrors(values) };
  };

  const serviceModeOptions = simplifiedServiceModesForSupport.map((mode, key) => ({
    index: key,
    value: formatMessage({ id: mode }),
    label: formatMessage({ id: mode }),
  }));
  const noServiceModeFromOrder = serviceMode === serializeServiceMode(null);

  // auto open the store locator if coming back from logging in inside the eco app webview
  useEffect(() => {
    const autoOpen = sessionStorage.getItem('autoOpenLocation') === 'true';
    if (autoOpen && locationFieldRef.current) {
      sessionStorage.removeItem('autoOpenLocation');
      setIsRestaurantSelectorShowing(true);
    }
  }, []);

  return (
    <>
      {!fromApp && (
        <Box margin="2rem 0 1rem" width="100%" center>
          <ActionLink
            size={ActionButtonSizes.SMALL}
            to={routes.support}
            variant={ActionButtonVariants.OUTLINE}
          >
            {formatMessage({ id: 'backToTopics' })}
          </ActionLink>
        </Box>
      )}
      <ContentWrap>
        <PageHeading>
          {smallTitle && <small>{smallTitle}</small>}
          <br />
          {bigTitle}
        </PageHeading>

        {alertMessage && (
          <SupportAlertBox alertStyle={ALERT_STYLE.error} testId="ineligible-for-refund">
            {alertMessage}
          </SupportAlertBox>
        )}
      </ContentWrap>

      {faqs.length > 0 && (
        <>
          <SectionHeading>{formatMessage({ id: 'faq' })}</SectionHeading>
          <Accordion>
            {faqs.map((faq: IFaq, i) => (
              <AccordionItem
                activeHighlight={true}
                title={faq.title}
                key={i}
                onInteract={(isExpanded, isFirstInteraction) =>
                  onFaqInteraction(isExpanded, isFirstInteraction, faq)
                }
              >
                <SanityBlockRenderer
                  content={faq.content as any}
                  fromApp={fromApp}
                  username={initialValues.name}
                  useremail={initialValues.email}
                />
              </AccordionItem>
            ))}
          </Accordion>
        </>
      )}

      <ContentWrap>
        <NeedHelpHeading>{formatMessage({ id: 'needMoreHelpSendUsAMessage' })}</NeedHelpHeading>

        <Formik
          initialValues={initialValues}
          onSubmit={handleSupportFormSubmit}
          validate={validateForm}
          enableReinitialize
          validateOnMount
        >
          {({ setFieldValue }) => {
            return (
              <Form translate="yes">
                <Field
                  name="name"
                  component={FormikTextInput}
                  label={formatMessage({ id: 'name' })}
                  autoFocus
                  data-testid="name-input"
                />
                {orderDate && (
                  <Field
                    name="orderDate"
                    component={FormikTextInput}
                    data-testid="order-date-field"
                    label={formatMessage({ id: 'orderDate' })}
                    value={`${formatTime(orderDate)} ${formatDate(orderDate)}`}
                    disabled
                  />
                )}
                <Field
                  name="email"
                  component={FormikTextInput}
                  label={formatMessage({ id: 'yourEmail' })}
                  disabled={initialValues.email}
                  data-testid="email-input"
                  fromApp={fromApp}
                />
                {showRestaurantSelector && (
                  <>
                    <Field name="storeAddress">
                      {(props: FieldProps) => (
                        <FormikTextInput
                          {...props}
                          value={selectedStoreAddress}
                          data-testid="location-selector"
                          onChange={noop}
                          label={formatMessage({ id: 'location' })}
                          ref={locationFieldRef}
                          onFocus={() => {
                            locationFieldRef.current?.blur();
                            setIsRestaurantSelectorShowing(true);
                          }}
                        />
                      )}
                    </Field>
                    <Field name="storeId" type="hidden" data-testid="store-id" />
                    <Field
                      name="orderDateTime"
                      data-testid="order-date-time-input"
                      // this is just a datepicker for now,
                      // but we may add time support in the future
                      // hence the name `orderDateTime`
                      component={FormikDateInput}
                      label={formatMessage({ id: 'orderDate' })}
                    />
                  </>
                )}
                {isRestaurantSelectorShowing && (
                  <GoogleGeolocationLibraryLoader>
                    <StoreSelector
                      onDismiss={() => {
                        setIsRestaurantSelectorShowing(false);
                      }}
                      updateStore={(storeId: string) => updateStore(storeId, setFieldValue)}
                    />
                  </GoogleGeolocationLibraryLoader>
                )}
                <Field
                  name="comments"
                  data-testid="comments-input"
                  component={FormikTextArea}
                  label={formatMessage({
                    id: showRestaurantSelector
                      ? 'describeIssueAndIncludePhoneNumber'
                      : 'describeIssue',
                  })}
                />
                {noServiceModeFromOrder && showServiceModeSelector && (
                  <Field
                    name="serviceMode"
                    data-testid="service-mode-dropdown"
                    component={FormikSelect}
                    label={formatMessage({ id: 'pleaseSelectOne' })}
                    options={serviceModeOptions}
                    required
                  />
                )}
                <Field
                  component={FormikCheckbox}
                  name="agreesToTermsOfService"
                  aria-label={formatMessage({ id: 'agreeToTermsOfService' })}
                  label={<TermsOfServiceLabel />}
                  data-testid="faq-support-tos"
                />
                <StyledActionButton
                  type="submit"
                  data-testid="submit-button"
                  isLoading={createCaseState === CreateCaseState.LOADING}
                  fullWidth
                  disabled={!!caseReferenceNumber}
                >
                  {formatMessage({ id: 'submit' })}
                </StyledActionButton>
              </Form>
            );
          }}
        </Formik>

        <CreateSupportCaseFeedback
          ref={feedbackRef}
          createCaseState={createCaseState}
          errorMessage={createCaseError || formatMessage({ id: 'errorCreatingSupportCase' })}
          successMessage={formatMessage({ id: 'successfullyCreatedSupportCase' })}
          successMessage2={formatMessage({ id: 'highVolumeMessage' })}
          successMessage3={formatMessage({ id: 'waitForReply' })}
          backlogMessage={useHighVolumeSupportMessage}
        />
        {isKeyboardOpen && <MobileSpacer height={keyboardHeight} />}
      </ContentWrap>
    </>
  );
};
